// Traduccion del nombre de la categoria de una guia.
//
// Medido el 18/09/2026 contra el ambiente: `obtener/catalogo-guias` devuelve
// `categoryName` siempre en espanol ("Otro"), aunque la peticion lleve `lang=en`
// —el endpoint SI recibe el idioma—. Resultado: era el unico texto de la tarjeta
// que no obedecia al idioma, y la interfaz en ingles mostraba "Otro".
//
// Lo de fondo es que el catalogo lo devuelva traducido, igual que el resto de sus
// campos. Esto es el mismo tipo de respaldo que ya se escribio para eventos, y se
// retira el dia que backend lo resuelva: basta con dejar de llamar a esta funcion.
//
// Mismo patron que `eventCategories` y `nationalities`: esquema remoto primero,
// respaldo local despues y, si la categoria no se reconoce, el texto tal como
// llega — nunca vacio.
//
// Los diccionarios de eventos se REUTILIZAN en vez de copiarse: traducen nombres
// de categoria, que es el mismo problema de idioma, y sus claves ya estan
// verificadas contra /api/lang/sch. Aqui solo se agregan las que son propias de
// guias.
import {
  EVENT_CATEGORY_FALLBACK,
  EVENT_CATEGORY_TEXT_KEYS,
} from "./eventCategories";

// Sin acentos y en minusculas, para que el catalogo pueda escribir "Tramites" o
// "TRÁMITES" y siga resolviendo.
const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

// Categorias vistas en el catalogo de guias que no existen en el de eventos.
// La lista esta incompleta a proposito: no hay endpoint que publique el catalogo
// de categorias de guias —llegan embebidas en cada fila—, asi que solo se dan de
// alta las que se han visto de verdad. Una categoria desconocida se muestra tal
// cual, que es exactamente lo que pasaba antes, de modo que ampliar esta tabla
// nunca puede romper nada.
export const GUIDE_ONLY_CATEGORY_FALLBACK = {
  tramites: { es: "Trámites", en: "Paperwork" },
};

export const resolveGuideCategoryLabel = (
  rawLabel,
  { texts, isEnglish } = {}
) => {
  const key = normalize(rawLabel);
  if (!key) return rawLabel ?? "";

  // 1. El esquema remoto manda cuando la categoria tiene clave publicada.
  const schemeKey = EVENT_CATEGORY_TEXT_KEYS[key];
  const fromScheme = schemeKey ? texts?.[schemeKey] : null;
  if (fromScheme) return fromScheme;

  // 2. Respaldo local. Cubre tambien las que tienen clave, porque el esquema se
  //    carga de forma asincrona: sin esto, las primeras pinturas de la tarjeta
  //    mostrarian el espanol dentro de la interfaz en ingles.
  const fallback =
    GUIDE_ONLY_CATEGORY_FALLBACK[key] ?? EVENT_CATEGORY_FALLBACK[key];
  if (fallback) return isEnglish ? fallback.en : fallback.es;

  // 3. Categoria que nadie ha dado de alta: se muestra como llega.
  return rawLabel;
};
